import React from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { DarkTheme } from './components/Theme/DarkTheme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Auth0Provider } from '@auth0/auth0-react';
import AuthTokenLayer from './AuthTokenLayer';
import { useNavigate } from 'react-router-dom';
import type { AppState } from '@auth0/auth0-react';
import { AppRoutes } from './Routes';

const MAX_RETRIES = 3;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: (failureCount: any, error: any) => {
				if (failureCount && failureCount > MAX_RETRIES) {
					return false;
				}
				const status = error.response?.status ?? 0;
				if (status < 500) {
					return false;
				}
				return true;
			},
		},
	},
});

function getBaseUrl() {
	if (process.env.NODE_ENV === 'development') {
		return 'http://localhost:3001';
	}
	const env = process.env.REACT_APP_ENV;
	if (env === 'prod') {
		return 'https://admin.arbinsights.com';
	}
	return `https://${env}.admin.arbinsights.com`;
}

function Auth0ProviderWithRedirectCallback({ children }: { children: React.ReactNode }) {
	const navigate = useNavigate();

	const onRedirectCallback = (appState?: AppState) => {
		navigate(appState?.returnTo || '/admin/users', { replace: true });
	};

	const redirectUri = getBaseUrl();
	const isDev = process.env.NODE_ENV === 'development';

	return (
		<Auth0Provider
			domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
			clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
			authorizationParams={{
				redirect_uri: redirectUri,
				audience: process.env.REACT_APP_AUTH0_AUDIENCE || '',
				scope: isDev ? 'openid profile email offline_access' : 'openid profile email',
			}}
			useRefreshTokens={isDev}
			cacheLocation={isDev ? 'localstorage' : 'memory'}
			onRedirectCallback={onRedirectCallback}
		>
			{children}
		</Auth0Provider>
	);
}

export default function App() {
	return (
		<Auth0ProviderWithRedirectCallback>
			<AuthTokenLayer />
			<QueryClientProvider client={queryClient}>
				<ThemeProvider theme={DarkTheme}>
					<CssBaseline />
					<AppRoutes />
				</ThemeProvider>
			</QueryClientProvider>
		</Auth0ProviderWithRedirectCallback>
	);
}
